import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { useNavigate } from "react-router-dom";
import { Trophy, Sparkles, Crown, Medal, Scale, ArrowRight } from "lucide-react";

const METAL = {
  0: { c: "#FFD24A", g: "linear-gradient(180deg,#FFE58A 0%,#C99A1E 100%)", glow: "rgba(255,210,74,0.45)" },
  1: { c: "#D9E1EE", g: "linear-gradient(180deg,#F1F5FB 0%,#8D98AB 100%)", glow: "rgba(217,225,238,0.35)" },
  2: { c: "#E0955A", g: "linear-gradient(180deg,#F2B585 0%,#9C5A2A 100%)", glow: "rgba(224,149,90,0.40)" },
};
function Initials({ name, color = "#CCFF00", size = 44 }) {
  const t = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span className="rounded-full flex items-center justify-center font-heading font-black shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36, color: "#050505", background: color, boxShadow: `0 0 18px ${color}66` }}>{t}</span>
  );
}

const WCLASS_LABEL = { u60: "≤60", u66: "60–66", u74: "66–74", u83: "74–83", u93: "83–93", u105: "93–105", o105: "105+" };

export default function Leaderboard() {
  const navigate = useNavigate();
  const { data: movements = [] } = useQuery({
    queryKey: ["lb-movements"],
    queryFn: async () => (await api.get("/api/leaderboard/movements")).data,
  });
  const [sel, setSel] = useState("");
  const [mode, setMode] = useState("absolute");
  const [wclass, setWclass] = useState(null);
  useEffect(() => { if (!sel && movements.length) setSel(movements[0].movement); }, [movements, sel]);
  useEffect(() => { setMode("absolute"); }, [sel]);

  const { data, isFetching } = useQuery({
    queryKey: ["lb", sel, mode, wclass],
    queryFn: async () => (await api.get("/api/leaderboard", { params: { movement: sel, mode, weight_class: wclass || undefined } })).data,
    enabled: !!sel,
  });
  const entries = data?.entries || [];
  const showPodium = entries.length >= 3;
  const podium = showPodium ? entries.slice(0, 3) : [];
  const rest = showPodium ? entries.slice(3) : entries;
  const relAvailable = data?.relative_available;
  const classes = data?.weight_classes || Object.keys(WCLASS_LABEL);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.strength} height="h-56 md:h-64" overline="the box · ranked" title="Squad Leaderboards"
        subtitle="See how your lifts and benchmark times stack up against the whole gym. Rank by raw numbers or strength-to-weight for a fair fight. OCTON hypes every mover."
        actions={
          movements.length > 0 && (
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-56 bg-[#0F1115] border-[#1E222A] h-10" data-testid="leaderboard-movement-select"><SelectValue placeholder="Pick a movement…" /></SelectTrigger>
              <SelectContent className="surface border-[#1E222A] max-h-72">
                {movements.map((m) => (
                  <SelectItem key={m.movement} value={m.movement}>{m.movement}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        } />

      {movements.length === 0 ? (
        <div className="relative overflow-hidden rounded-xl border border-[#1E222A] bg-gradient-to-b from-[#10131A] to-[#08090C] p-8 md:p-12 text-center">
          <div className="absolute left-1/2 -translate-x-1/2 -top-24 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: "#FFD24A" }} />
          <Trophy className="relative w-10 h-10 mx-auto mb-3" style={{ color: "#FFD24A", filter: "drop-shadow(0 0 12px rgba(255,210,74,0.7))" }} />
          <h3 className="relative font-heading font-black text-2xl md:text-3xl tracking-tight">The podium is empty. Claim it.</h3>
          <p className="relative text-[#8A91A6] mt-2 max-w-md mx-auto">Log your first lift with your OCTON coach and you'll be the one to beat.</p>
          <div className="relative grid grid-cols-3 gap-3 md:gap-5 items-end max-w-xl mx-auto mt-10">
            {[1, 0, 2].map((slot, i) => (
              <motion.div key={slot} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.1 }} className="flex flex-col items-center">
                <span className="w-12 h-12 rounded-full border-2 border-dashed mb-2 flex items-center justify-center font-heading font-black text-lg" style={{ borderColor: METAL[slot].c, color: METAL[slot].c }}>?</span>
                <div className={`w-full ${{ 0: "h-36", 1: "h-28", 2: "h-24" }[slot]} rounded-t-xl flex items-start justify-center pt-3`}
                  style={{ background: METAL[slot].g, boxShadow: `0 -10px 40px ${METAL[slot].glow}`, opacity: 0.85 }}>
                  <span className="font-heading font-black text-3xl text-black/60">{slot + 1}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <button onClick={() => navigate("/app/benchmarks?tab=lifts")} className="relative mt-8 inline-flex items-center gap-2 bg-acid text-black hover:bg-[#AACC00] rounded-full h-11 px-6 font-medium shadow-[0_0_30px_rgba(204,255,0,0.35)]">
            Log a lift <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          {/* Fairness controls */}
          <div className="surface rounded-lg p-4 flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-full border border-[#1E222A] p-1" data-testid="lb-mode-toggle">
              <button onClick={() => setMode("absolute")} data-testid="lb-mode-absolute"
                className={`rounded-full px-4 h-8 text-sm transition-colors ${mode === "absolute" ? "bg-acid text-black" : "text-[#8A91A6] hover:text-white"}`}>Absolute</button>
              <button onClick={() => relAvailable && setMode("relative")} disabled={!relAvailable} data-testid="lb-mode-relative"
                title={relAvailable ? "" : "Strength-to-weight applies to lifts only"}
                className={`rounded-full px-4 h-8 text-sm transition-colors inline-flex items-center gap-1.5 ${mode === "relative" ? "bg-acid text-black" : relAvailable ? "text-[#8A91A6] hover:text-white" : "text-[#3a3f4c] cursor-not-allowed"}`}>
                <Scale className="w-3.5 h-3.5" /> Per bodyweight</button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 ml-auto">
              <span className="mono-label mr-1">weight class</span>
              <button onClick={() => setWclass(null)} data-testid="wclass-all"
                className={`rounded-full px-3 h-7 text-xs border transition-colors ${!wclass ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:text-white"}`}>All</button>
              {classes.map((c) => (
                <button key={c} onClick={() => setWclass(c)} data-testid={`wclass-${c}`}
                  className={`rounded-full px-3 h-7 text-xs border transition-colors ${wclass === c ? "border-acid text-acid bg-acid/10" : "border-[#1E222A] text-[#8A91A6] hover:text-white"}`}>{WCLASS_LABEL[c] || c}<span className="text-[10px] ml-0.5">kg</span></button>
              ))}
            </div>
          </div>

          {/* OCTON hype banner */}
          {data?.hype && (
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              className="surface rounded-lg p-4 flex items-start gap-3 border border-neural/30" data-testid="leaderboard-hype">
              <Sparkles className="w-5 h-5 text-neural shrink-0 mt-0.5" />
              <p className="text-white/90 italic">{data.hype}</p>
            </motion.div>
          )}

          {/* Podium */}
          {showPodium && (
            <div className="grid grid-cols-3 gap-3 md:gap-4 items-end">
              {[1, 0, 2].map((slot) => {
                const e = podium[slot];
                if (!e) return <div key={slot} />;
                const heights = { 0: "h-44", 1: "h-32", 2: "h-28" };
                const m = METAL[slot];
                const Icon = slot === 0 ? Crown : Medal;
                return (
                  <motion.div key={slot} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: slot * 0.1, type: "spring", stiffness: 120 }}
                    className="flex flex-col items-center" data-testid={`podium-${e.rank}`}>
                    <Icon className="w-6 h-6 mb-1" style={{ color: m.c, filter: `drop-shadow(0 0 8px ${m.glow})` }} />
                    <Initials name={e.name} color={m.c} size={slot === 0 ? 56 : 46} />
                    <p className={`text-sm font-medium truncate max-w-full mt-2 ${e.is_me ? "text-acid" : ""}`}>{e.name}{e.is_me && " (you)"}</p>
                    <p className="font-heading font-black text-xl md:text-2xl mt-0.5" style={{ color: m.c }}>{e.display}</p>
                    {e.bodyweight && <p className="mono-label">{e.bodyweight}kg bw</p>}
                    <div className={`w-full ${heights[slot]} mt-2 rounded-t-xl flex items-start justify-center pt-3 ${e.is_me ? "ring-2 ring-acid" : ""}`}
                      style={{ background: m.g, boxShadow: `0 -12px 40px ${m.glow}` }}>
                      <span className="font-heading font-black text-4xl text-black/55">{e.rank}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Ranked list */}
          <div className="rounded-xl p-4 border border-[#1E222A] bg-gradient-to-b from-[#0F1115] to-[#0B0D11]">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-heading font-bold tracking-tight">{sel}</span>
              <span className="mono-label ml-auto">ranked by {data?.metric || "—"} · {entries.length} athlete{entries.length === 1 ? "" : "s"}</span>
            </div>
            {isFetching && entries.length === 0 ? (
              <p className="mono-label animate-pulse py-6 text-center">Loading rankings…</p>
            ) : (
              <div className="space-y-1.5">
                {rest.map((e) => (
                  <div key={e.user_id} data-testid={`rank-row-${e.rank}`}
                    className={`flex items-center gap-3 rounded-md px-3 py-2.5 border ${e.is_me ? "border-acid/50 bg-acid/5" : "border-transparent hover:border-[#1E222A]"}`}>
                    <span className="font-heading font-black text-lg w-8 text-[#565C6F]">{e.rank}</span>
                    <Initials name={e.name} color={e.is_me ? "#CCFF00" : "#3D7BFF"} size={32} />
                    <span className={`flex-1 text-sm ${e.is_me ? "text-acid font-medium" : "text-white/90"}`}>{e.name}{e.is_me && " (you)"}{e.bodyweight && <span className="text-[#565C6F] ml-2 font-mono2 text-xs">{e.bodyweight}kg</span>}</span>
                    <span className="font-mono2 text-sm text-white/90">{e.display}</span>
                  </div>
                ))}
                {entries.length === 0 && <p className="text-[#565C6F] text-sm py-4 text-center">No ranked results for this filter.</p>}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

